import { falArrowUpFromBracket } from 'shared/@generated/icons/falArrowUpFromBracket';
import { falDownload } from 'shared/@generated/icons/falDownload';
import { falLink } from 'shared/@generated/icons/falLink';
import { falSpinnerThird } from 'shared/@generated/icons/falSpinnerThird';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Listing } from 'shared/@types';
import logo from 'shared/assets/logo.png';
import { Button } from 'shared/components/button';
import { BaseModal, BaseModalRef } from 'shared/components/base-modal';
import { Icon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import { Text } from 'shared/components/text';
import { analytics } from 'shared/library/analytics';
import { getListingShareUrl, type ListingSharePlacement } from 'shared/library/listing-share';
import { getCountryName } from 'shared/library/i18n/country-name';
import { notify } from 'shared/library/notify';
import { useRoute } from 'shared/library/routes/route';
import { useFormatter } from 'shared/library/use-formatter';
import { clsx } from 'clsx';
import { useUserStore } from 'shared/state/user';

type ListingShareModalProps = {
  listing: Listing;
  placement?: ListingSharePlacement;
  dense?: boolean;
  iconOnly?: boolean;
  className?: string;
  buttonColor?: 'primary' | 'secondary' | 'brown' | 'white' | 'boost';
};

const ListingShareModal = ({
  listing,
  placement = 'listing_detail',
  dense = true,
  iconOnly = false,
  className,
  buttonColor = 'white',
}: ListingShareModalProps) => {
  const { t } = useTranslation('components');
  const { formatCurrency, formatDateCustom } = useFormatter();
  const route = useRoute();
  const isOwner = useUserStore((state) => state.user?.id === listing.user.id);
  const sharerRole = isOwner ? 'owner' : 'visitor';
  const eventParams = { listing_id: listing.id, placement, sharer_role: sharerRole };
  const modalRef = useRef<BaseModalRef>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [hasTriedAutoGenerate, setHasTriedAutoGenerate] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);

  const listingUrl = useMemo(
    () => route('listings.show', { listing: listing.slug }, { includeDomain: true }),
    [route, listing.slug],
  );
  const shareUrls = useMemo(
    () => ({
      copy: getListingShareUrl(listingUrl, listing.id, 'copy', placement, sharerRole),
      qr: getListingShareUrl(listingUrl, listing.id, 'qr', placement, sharerRole),
      native: getListingShareUrl(listingUrl, listing.id, 'native', placement, sharerRole),
    }),
    [listingUrl, listing.id, placement, sharerRole],
  );

  useEffect(() => {
    setImageDataUrl(null);
    setHasTriedAutoGenerate(false);
  }, [shareUrls.qr]);

  const listingPrice = useMemo(() => {
    if (listing.price === null || listing.price === undefined || !listing.currency) {
      return '';
    }

    return formatCurrency(listing.price, listing.currency);
  }, [formatCurrency, listing.currency, listing.price]);

  const listingBirthDate = useMemo(() => {
    if (!listing.birth_date) return '';

    return formatDateCustom(listing.birth_date);
  }, [formatDateCustom, listing.birth_date]);

  const listingLocation = useMemo(() => {
    return [listing.user.city, getCountryName(t, listing.user.country)].filter(Boolean).join(', ');
  }, [listing.user.city, listing.user.country, t]);

  const listingReadyToMoveDate = useMemo(() => {
    if (!listing.pickup_date) return '';

    return formatDateCustom(listing.pickup_date);
  }, [formatDateCustom, listing.pickup_date]);

  const socialText = useMemo(
    () => t('listing_share_modal.share_text', { breed: listing.breed_name }),
    [listing.breed_name, t],
  );

  const sellerName = useMemo(() => {
    const kennelName = listing.user.breeder?.name?.trim();

    if (kennelName) return kennelName;

    const displayName = listing.user.display_name?.trim();

    if (displayName) return displayName;

    const fullName = listing.user.name?.trim();

    if (fullName) return fullName;

    return listing.user.breeder_id || listing.user.is_breeder
      ? t('common:breeder')
      : t('common:private_seller');
  }, [
    listing.user.breeder?.name,
    listing.user.display_name,
    listing.user.name,
    listing.user.breeder_id,
    listing.user.is_breeder,
    t,
  ]);

  const ensureImageDataUrl = useCallback(async () => {
    if (imageDataUrl) return imageDataUrl;

    setIsGeneratingImage(true);

    try {
      const { buildListingShareImage } = await import('shared/library/listing-share-image');
      const generatedImage = await buildListingShareImage({
        listingImageUrl: listing.media_assets[0]?.url ?? listing.avatar?.url ?? '',
        logoUrl: resolveAssetUrl(logo),
        listingUrl: shareUrls.qr,
        breedName: listing.breed_name,
        priceLabel: t('listing_share_modal.price_label'),
        price: listingPrice,
        birthDateLabel: t('listing_share_modal.birth_date_label'),
        birthDate: listingBirthDate,
        readyToMoveLabel: t('listing_share_modal.ready_to_move_label'),
        readyToMoveDate: listingReadyToMoveDate,
        locationLabel: t('listing_share_modal.location_label'),
        location: listingLocation,
        sellerName,
      });

      setImageDataUrl(generatedImage);

      return generatedImage;
    } finally {
      setIsGeneratingImage(false);
    }
  }, [
    imageDataUrl,
    listing.media_assets,
    listing.avatar?.url,
    shareUrls.qr,
    listing.breed_name,
    t,
    listingPrice,
    listingBirthDate,
    listingReadyToMoveDate,
    listingLocation,
    sellerName,
  ]);

  useEffect(() => {
    if (!isOpen || imageDataUrl || isGeneratingImage || hasTriedAutoGenerate) return;

    setHasTriedAutoGenerate(true);
    ensureImageDataUrl().catch(() => {
      notify.error(
        { description: t('listing_share_modal.image_generation_error') },
        'listing-share-modal.tsx - ensureImageDataUrl',
      );
    });
  }, [ensureImageDataUrl, hasTriedAutoGenerate, imageDataUrl, isGeneratingImage, isOpen, t]);

  async function onShare() {
    if (typeof navigator === 'undefined' || !navigator.share) {
      await onCopyLink();
      return;
    }

    try {
      let sharedAsFile = false;
      const dataUrl = await ensureImageDataUrl();

      if (dataUrl) {
        const file = await dataUrlToFile(dataUrl, `getapet-${listing.slug}.png`);

        if (canShareFiles([file])) {
          const result = await tryNativeShare({
            title: listing.breed_name,
            text: socialText,
            url: shareUrls.native,
            files: [file],
          });

          if (result === 'aborted') return;

          sharedAsFile = result === 'shared';
        }
      }

      if (!sharedAsFile) {
        const result = await tryNativeShare({
          title: listing.breed_name,
          text: socialText,
          url: shareUrls.native,
        });

        if (result === 'aborted') return;

        if (result === 'unsupported') {
          await onCopyLink();
          return;
        }
      }

      analytics.event('share_success', {
        ...eventParams,
        method: 'native',
        mode: sharedAsFile ? 'file' : 'link',
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      notify.error(
        { description: t('listing_share_modal.share_error') },
        'listing-share-modal.tsx - onShare',
      );
    }
  }

  async function onCopyLink() {
    try {
      await copyToClipboard(shareUrls.copy);
      notify.success({ description: t('listing_share_modal.link_copied') });
      analytics.event('share_copy', { ...eventParams, method: 'copy' });
    } catch {
      notify.error(
        { description: t('listing_share_modal.copy_error') },
        'listing-share-modal.tsx - onCopyLink',
      );
    }
  }

  async function onDownloadImage() {
    try {
      const dataUrl = await ensureImageDataUrl();

      if (!dataUrl) {
        throw new Error('Missing generated image');
      }

      downloadDataUrl(dataUrl, `getapet-${listing.slug}.png`);
      notify.success({ description: t('listing_share_modal.image_downloaded') });
      analytics.event('share_download', { ...eventParams, method: 'qr' });
    } catch {
      notify.error(
        { description: t('listing_share_modal.download_error') },
        'listing-share-modal.tsx - onDownloadImage',
      );
    }
  }

  function onOpenModal(open: () => void) {
    analytics.event('share_open', eventParams);
    setHasTriedAutoGenerate(false);
    setIsOpen(true);
    open();
  }

  return (
    <BaseModal
      ref={modalRef}
      onClose={() => setIsOpen(false)}
      title={t('listing_share_modal.title')}
      description={t('listing_share_modal.subtitle')}
      footer={
        <View className={'gap-1.5'}>
          <Button
            text={t('listing_share_modal.share_now')}
            icon={falArrowUpFromBracket}
            onPress={() => void onShare()}
          />
          <Button
            text={t('listing_share_modal.copy_link')}
            icon={falLink}
            color={'white'}
            onPress={() => void onCopyLink()}
          />
          <Button
            text={t('listing_share_modal.download_image')}
            icon={falDownload}
            color={'white'}
            onPress={() => void onDownloadImage()}
          />
        </View>
      }
      trigger={({ open }) => (
        <>
          {iconOnly ? (
            <TouchableOpacity
              onPress={() => onOpenModal(open)}
              className={clsx(
                'shadow-brand-brown-dark/15 flex-row items-center justify-center rounded-full bg-white p-3 shadow hover:opacity-75',
                className,
              )}
            >
              <Icon icon={falArrowUpFromBracket} className={'text-stone-900'} />
            </TouchableOpacity>
          ) : (
            <Button
              text={t('listing_share_modal.share_listing')}
              icon={falArrowUpFromBracket}
              iconPosition={'left'}
              color={buttonColor}
              dense={dense}
              center={true}
              className={className}
              onPress={() => onOpenModal(open)}
            />
          )}
        </>
      )}
    >
      <View className={'overflow-hidden rounded-xl'}>
        {imageDataUrl ? (
          <Image
            src={imageDataUrl as any}
            alt={listing.breed_name}
            containerClassName={'aspect-[4/5] w-full'}
            imageClassName={'object-cover'}
          />
        ) : (
          <View
            className={'bg-brand-brown-dark/95 aspect-[4/5] w-full items-center justify-center'}
          >
            {listing.media_assets[0]?.url && (
              <Image
                src={listing.media_assets[0]}
                alt={listing.breed_name}
                containerClassName={'absolute inset-0 size-full'}
                imageClassName={'object-cover opacity-25'}
                sizeType={'large'}
              />
            )}

            <View className={'items-center gap-2 p-6'}>
              <Icon
                icon={falSpinnerThird}
                className={clsx('size-8 text-white', { 'animate-spin': isGeneratingImage })}
              />

              <Text className={'text-center text-sm font-semibold text-white'}>
                {t('listing_share_modal.image_preparing')}
              </Text>
            </View>
          </View>
        )}
      </View>
    </BaseModal>
  );
};

async function dataUrlToFile(dataUrl: string, fileName: string) {
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  return new File([blob], fileName, { type: 'image/png' });
}

function downloadDataUrl(dataUrl: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = dataUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

async function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'absolute';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand('copy');
  input.remove();

  if (!copied) {
    throw new Error('Copy failed');
  }
}

function canShareFiles(files: File[]) {
  try {
    return navigator.canShare?.({ files }) ?? false;
  } catch {
    return false;
  }
}

async function tryNativeShare(data: ShareData): Promise<'shared' | 'aborted' | 'unsupported'> {
  try {
    await navigator.share(data);
    return 'shared';
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return 'aborted';
    }

    if (isUnsupportedNativeShareError(error)) {
      return 'unsupported';
    }

    throw error;
  }
}

function isUnsupportedNativeShareError(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  return [
    'InvalidStateError',
    'NotAllowedError',
    'NotSupportedError',
    'SecurityError',
    'TypeError',
  ].includes(error.name);
}

function resolveAssetUrl(asset: any) {
  return typeof asset === 'string' ? asset : (asset?.src ?? '');
}

export { ListingShareModal };
